import type { LoginMenu } from '@features/auth'
import {
  normalizeLegacyMenuPath,
  normalizeRoutePath,
} from '@/shared/navigation/menuPathResolver'

export function menuHref(pageName: string): string {
  return normalizeLegacyMenuPath(pageName)
}

export function isMenuPathActive(pathname: string, pageName: string): boolean {
  const href = menuHref(pageName)
  if (href === '#') {
    return false
  }

  const path = normalizeRoutePath(pathname)
  const target = normalizeRoutePath(href)

  if (target === '/welcome') {
    return path === '/welcome' || path === '/'
  }

  return path === target || path.startsWith(`${target}/`)
}

export function menuLabel(item: LoginMenu): string {
  return item.displayName?.trim() || item.pageName || 'Menu'
}

export function sortMenus(items: LoginMenu[]): LoginMenu[] {
  return [...items].sort((a, b) => a.displayOrder - b.displayOrder)
}

export function sortMenuParents(items: LoginMenu[]): LoginMenu[] {
  return sortMenus(items)
}

export function findActiveMenuTrail(
  items: LoginMenu[],
  pathname: string,
  ancestors: LoginMenu[] = [],
): LoginMenu[] | null {
  for (const item of sortMenus(items)) {
    const trail = [...ancestors, item]
    const children = item.children ?? []

    if (children.length > 0) {
      const nested = findActiveMenuTrail(children, pathname, trail)
      if (nested) return nested
    }

    if (isMenuPathActive(pathname, item.pageName)) {
      return trail
    }
  }

  return null
}

function collapseMenuTrail(trail: LoginMenu[]): LoginMenu[] {
  if (trail.length <= 1) return trail

  const withoutDuplicateLabels = trail.filter((item, index) => {
    if (index === 0) return true
    return menuLabel(item) !== menuLabel(trail[index - 1])
  })

  if (withoutDuplicateLabels.length <= 1) {
    return withoutDuplicateLabels
  }

  const hrefs = withoutDuplicateLabels.map((item) => menuHref(item.pageName))
  if (hrefs.every((href) => href === hrefs[0])) {
    return [withoutDuplicateLabels[withoutDuplicateLabels.length - 1]]
  }

  return withoutDuplicateLabels
}

export function menuTrailLabels(items: LoginMenu[], pathname: string): string[] {
  const trail = findActiveMenuTrail(items, pathname)
  if (trail?.length) {
    return collapseMenuTrail(trail).map(menuLabel)
  }

  if (pathname === '/' || pathname === '/welcome') {
    return ['Welcome']
  }

  const segment = pathname.split('/').filter(Boolean)[0]
  if (!segment) return []

  return [
    segment
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[-_]/g, ' ')
      .trim(),
  ]
}
